import Phaser from 'phaser';
import { TEXTURE_KEYS, DEPTHS } from '../systems/util/constants.js';

const MOVE_SPEED = 210;
const AIR_MOVE_SPEED = 190;
const ACCEL_LERP_GROUND = 0.28;
const ACCEL_LERP_AIR = 0.18;
const DECEL_LERP = 0.32;

const JUMP_VELOCITY = -520;
const JUMP_CUT_MULTIPLIER = 0.5;
const COYOTE_MS = 110;
const JUMP_BUFFER_MS = 120;

const WALL_SLIDE_MAX_FALL = 90;
const WALL_JUMP_VELOCITY_X = 300;
const WALL_JUMP_VELOCITY_Y = -480;
const WALL_JUMP_LOCK_MS = 160;

const SHOOT_COOLDOWN_MS = 420;
const PROJECTILE_SPEED = 520;
const PROJECTILE_DAMAGE = 1;

const MAX_AMMO = 6;
const MAG_REFILL_AMOUNT = 3;

const MAX_HP = 5;
const INVULN_MS = 900;
const HIT_KNOCKBACK_X = 220;
const HIT_KNOCKBACK_Y = -180;

// Comic-art poses: single static images (no animation frames), swapped by
// state.
//
// IMPORTANT Arcade Physics quirk (verified against Phaser's own source,
// physics/arcade/Body.js): Body.setSize()/setOffset() do NOT take final,
// on-screen pixel values. They take NATIVE (pre-scale) texture pixels —
// Phaser automatically multiplies both by the GameObject's current scale
// every physics step (`body.width = body.sourceWidth * scaleX`, and
// similarly offset is scaled before being added to the body's position).
// Passing already-scaled numbers (as an earlier version of this file did)
// means Phaser scales them AGAIN, shrinking the real collision box to
// scale^2 of what was intended (here, 0.2 * 0.2 — a ~3x6px box instead of
// 14x30) and mispositioning it. BODY_W/H and the offsets below are sized
// in native pixels so that, after Phaser's automatic *POSE_SCALE, the
// final on-screen box is the actual intended 14x30, correctly centered on
// each pose's real drawn content (not the full padded canvas).
// Bumped substantially (was 0.2) — the reference look has the character as
// the dominant visual element (roughly as tall as the floor structure
// itself), not a small sprite at the bottom of the screen. Only this scale
// changes; FINAL_BODY_W/H (the actual gameplay hitbox) stay exactly as
// tuned, since the fix above already decouples visual scale from collision
// size — bigger art doesn't change jump feel or platform clearance.
const POSE_SCALE = 0.503;
const FINAL_BODY_W = 14;
const FINAL_BODY_H = 30;
const BODY_W = FINAL_BODY_W / POSE_SCALE;
const BODY_H = FINAL_BODY_H / POSE_SCALE;
// [x0, y0, x1, y1] content bounding box measured from each source PNG,
// NOT the full image canvas — every pose has several px of transparent
// margin below the drawn feet (measured via PIL getbbox()), and Phaser's
// sprite origin/positioning is based on the full canvas size regardless.
const POSE_BBOX = {
  idle: [9, 7, 75, 186],
  run: [6, 21, 136, 204],
  jump: [5, 14, 126, 177],
  shoot: [9, 8, 155, 193]
};
// Muzzle height above the feet, in native px: the vertical midpoint of the
// shoot pose's own content box (chest height), not the exact gun-barrel
// pixel. This has to be derived from the art, not guessed, because `this.y`
// is the bottom-anchored origin (see setOrigin below) — firing at a fixed
// "this.y - 2" put the muzzle at ground level, where the shot's own floor-
// collider (Game.js) recycled it before it ever became visible.
// Chest-height rather than the literal (higher) barrel pixel on purpose —
// ground enemies stand under low platforms with only ~64px of clearance
// above the floor, so their hurtboxes (Enemy.js's ENEMY_CONFIGS bodySize)
// can't be taller than that without risking getting stuck; chest height
// keeps the beam within a range enemy hurtboxes can actually cover while
// staying comfortably clear of that ceiling.
const SHOOT_MUZZLE_NATIVE_ABOVE_FEET = (POSE_BBOX.shoot[3] - POSE_BBOX.shoot[1]) / 2;
const POSE_TEXTURES = {
  idle: TEXTURE_KEYS.PLAYER,
  run: TEXTURE_KEYS.PLAYER_RUN,
  jump: TEXTURE_KEYS.PLAYER_JUMP,
  shoot: TEXTURE_KEYS.PLAYER_SHOOT
};
// How long after firing the shoot pose lingers, so a single tap still reads
// as a visible shooting frame instead of flicking back to run/idle same-frame.
const SHOOT_POSE_HOLD_MS = 160;

export class Player extends Phaser.Physics.Arcade.Sprite {
  constructor(scene, x, y, { projectilePool, wallJumpEnabled = true } = {}) {
    super(scene, x, y, TEXTURE_KEYS.PLAYER);
    scene.add.existing(this);
    scene.physics.add.existing(this);

    this.projectilePool = projectilePool;
    this.wallJumpEnabled = wallJumpEnabled;

    this.setDepth(DEPTHS.PLAYER);
    this.setCollideWorldBounds(true);
    this.setScale(POSE_SCALE);
    // Bottom-anchored instead of Phaser's default center origin — this.y
    // now tracks each pose's own CANVAS bottom edge rather than its
    // center. That matters because Phaser derives the body's world
    // position from `offset - displayOrigin`, and displayOrigin is
    // `originFraction * currentFrameHeight`. With center origin (0.5),
    // that meant 0.5 * height, and height swings 185-212px native across
    // poses — a big, pose-dependent term that nudged the collision box's
    // computed position every time the pose (texture) changed, e.g. every
    // time movement crossed the run/idle threshold. With bottom origin
    // (1.0), the equivalent term becomes just the padding *below the
    // feet*, which is nearly identical across all 4 poses (6-8px, not
    // 185-212px) — shrinking that per-pose jitter to under 1.5px instead
    // of several times that. Likely the actual cause of "falls through
    // after walking around" — a repeated small collision correction on
    // every pose swap, not a one-time positioning error.
    this.setOrigin(0.5, 1);
    this.body.setSize(BODY_W, BODY_H);
    this.body.setMaxVelocity(400, 900);

    this._pose = null;
    this._shootPoseTimer = 0;
    this._airborneMs = 0;
    this._setPose('idle');
    this._landOnSurface(y);

    this.setData('team', 'player');
    this.setData('invulnerable', false);

    this.facing = 1;
    this.hp = MAX_HP;
    this.maxHp = MAX_HP;
    this.ammo = MAX_AMMO;
    this.maxAmmo = MAX_AMMO;
    this.alive = true;

    this._coyoteTimer = 0;
    this._jumpBufferTimer = 0;
    this._shootCooldown = 0;
    this._invulnTimer = 0;
    this._wallJumpLockTimer = 0;
    this._wallSide = 0;
    this.isWallSliding = false;
  }

  get isGrounded() {
    return this.body.blocked.down;
  }

  takeDamage(amount, sourceX = this.x) {
    if (!this.alive || this.getData('invulnerable')) return;
    this.hp -= amount;
    this._invulnTimer = INVULN_MS;
    this.setData('invulnerable', true);

    const dir = this.x < sourceX ? -1 : 1;
    this.body.setVelocity(dir * HIT_KNOCKBACK_X, HIT_KNOCKBACK_Y);

    this.scene.tweens.add({
      targets: this,
      alpha: 0.35,
      duration: 80,
      yoyo: true,
      repeat: Math.floor(INVULN_MS / 160)
    });

    if (this.hp <= 0) {
      this.hp = 0;
      this._die();
    }
    this.scene.events.emit('player-hp-changed', this.hp, this.maxHp);
  }

  heal(amount) {
    this.hp = Math.min(this.maxHp, this.hp + amount);
    this.scene.events.emit('player-hp-changed', this.hp, this.maxHp);
  }

  refillAmmo(amount = MAG_REFILL_AMOUNT) {
    this.ammo = Math.min(this.maxAmmo, this.ammo + amount);
    this.scene.events.emit('player-ammo-changed', this.ammo, this.maxAmmo);
  }

  _setPose(pose) {
    if (this._pose === pose) return;
    this._pose = pose;
    this.setTexture(POSE_TEXTURES[pose]);
    // Offset is in native pixels (see the big comment above) — with the
    // bottom-anchored origin set in the constructor, the resulting world
    // position now only swings by the small (~2px native) per-pose
    // difference in padding-below-the-feet, not the frame-height swing a
    // center origin would produce.
    const [x0, , x1, y1] = POSE_BBOX[pose];
    const centerX = (x0 + x1) / 2;
    this.body.setOffset(centerX - BODY_W / 2, y1 - BODY_H);
  }

  // Same fix as Enemy.js's spawn-position correction, and for the same
  // reason: `surfaceSpawnY` (from level data — playerStart, or the same
  // coordinate again on fall-respawn) is a tile-row CENTER that assumes a
  // roughly 32px-tall sprite ("center + 16px = floor surface"). At the
  // current (much bigger) POSE_SCALE, the real feet are far below that, so
  // using the raw spawn Y embeds the player in solid ground — which Arcade
  // Physics doesn't resolve the way it resolves a body colliding while
  // moving, so it just falls through instead of landing. This was already
  // fixed for enemies but never for the player itself.
  //
  // With the bottom-anchored origin, `this.y` is the sprite's rendered
  // BOTTOM edge (canvas bottom), which sits (height - y1) native px below
  // the actual content bottom (the feet) — so this solves for the this.y
  // that puts the feet exactly on the intended surface line.
  _landOnSurface(surfaceSpawnY) {
    const [, , , y1] = POSE_BBOX[this._pose];
    this.y = surfaceSpawnY + 16 + POSE_SCALE * (this.height - y1);
    this.body.updateFromGameObject();
  }

  /** Repositions the player (level start, or fall-into-pit respawn) with the same surface-landing correction as spawn. */
  respawnAt(x, y) {
    this.x = x;
    this._landOnSurface(y);
  }

  _die() {
    this.alive = false;
    this.setVelocity(0, 0);
    this.body.enable = false;
    this.setTint(0xff3355);
    this.scene.events.emit('player-died');
  }

  update(time, delta, input) {
    if (!this.alive) return;

    const dt = delta;
    const body = this.body;

    if (this._invulnTimer > 0) {
      this._invulnTimer -= dt;
      if (this._invulnTimer <= 0) {
        this.setData('invulnerable', false);
        this.setAlpha(1);
        this.scene.tweens.killTweensOf(this);
      }
    }

    // --- Wall detection (airborne only) ---
    const touchingWall = !this.isGrounded && (body.blocked.left || body.blocked.right);
    this._wallSide = body.blocked.left ? -1 : body.blocked.right ? 1 : 0;
    this.isWallSliding = this.wallJumpEnabled && touchingWall && body.velocity.y > 0;
    if (this.isWallSliding) {
      body.velocity.y = Math.min(body.velocity.y, WALL_SLIDE_MAX_FALL);
    }

    // --- Horizontal movement ---
    if (this._wallJumpLockTimer > 0) {
      this._wallJumpLockTimer -= dt;
    } else {
      const targetSpeed = (this.isGrounded ? MOVE_SPEED : AIR_MOVE_SPEED) * input.moveX;
      const lerpT = input.moveX !== 0 ? (this.isGrounded ? ACCEL_LERP_GROUND : ACCEL_LERP_AIR) : DECEL_LERP;
      body.velocity.x = Phaser.Math.Linear(body.velocity.x, targetSpeed, lerpT);
      if (input.moveX !== 0) this.facing = input.moveX;
    }
    this.setFlipX(this.facing < 0);

    // --- Jump (coyote time + input buffer) ---
    this._coyoteTimer = this.isGrounded ? COYOTE_MS : this._coyoteTimer - dt;
    this._jumpBufferTimer = input.jumpPressed ? JUMP_BUFFER_MS : this._jumpBufferTimer - dt;

    if (this._wallJumpLockTimer <= 0 && input.jumpPressed && this.isWallSliding) {
      body.velocity.x = -this._wallSide * WALL_JUMP_VELOCITY_X;
      body.velocity.y = WALL_JUMP_VELOCITY_Y;
      this.facing = -this._wallSide;
      this._wallJumpLockTimer = WALL_JUMP_LOCK_MS;
      this._jumpBufferTimer = 0;
      this._coyoteTimer = 0;
    } else if (this._jumpBufferTimer > 0 && this._coyoteTimer > 0) {
      body.velocity.y = JUMP_VELOCITY;
      this._jumpBufferTimer = 0;
      this._coyoteTimer = 0;
    } else if (!input.jumpDown && body.velocity.y < 0) {
      body.velocity.y *= JUMP_CUT_MULTIPLIER ** (dt / 16.7); // frame-rate-independent-ish cut
    }

    // --- Shooting ---
    this._shootCooldown -= dt;
    if (input.shootDown && this._shootCooldown <= 0 && this.projectilePool) {
      if (this.ammo > 0) {
        this._shootCooldown = SHOOT_COOLDOWN_MS;
        const muzzleX = this.x + this.facing * 16;
        const muzzleY = this.y - SHOOT_MUZZLE_NATIVE_ABOVE_FEET * POSE_SCALE;
        this.projectilePool.fire(muzzleX, muzzleY, this.facing * PROJECTILE_SPEED, 0, {
          damage: PROJECTILE_DAMAGE
        });
        this.ammo -= 1;
        this._shootPoseTimer = SHOOT_POSE_HOLD_MS;
        this.scene.events.emit('player-ammo-changed', this.ammo, this.maxAmmo);
        this.scene.events.emit('player-shot');
      } else {
        // short cooldown so an empty-trigger tap doesn't spam the event/sfx
        this._shootCooldown = 150;
        this._shootPoseTimer = SHOOT_POSE_HOLD_MS;
        this.scene.events.emit('player-shoot-empty');
      }
    }
    this._shootPoseTimer -= dt;

    // --- Pose (single static image per state, no animation frames) ---
    // Debounced separately from the coyote-time logic above (which is
    // about input forgiveness, not visuals): a staticGroup floor made of
    // adjacent bodies can occasionally report body.blocked.down false for
    // a single stray frame while still resting on the ground (a milder
    // symptom of the same seam-collision class of issue fixed on the tile
    // side) — without this, that one frame swaps to the very
    // different-looking "jump" pose and immediately back, which reads as
    // a flicker. A real jump/fall stays airborne far longer than this.
    this._airborneMs = this.isGrounded ? 0 : this._airborneMs + dt;
    if (this._airborneMs > 60) {
      this._setPose('jump');
    } else if (this._shootPoseTimer > 0) {
      this._setPose('shoot');
    } else if (Math.abs(body.velocity.x) > 15) {
      this._setPose('run');
    } else {
      this._setPose('idle');
    }

    // --- Fell into a pit: soft respawn handled by scene via event ---
    // Checked against the level's visible height, not physics.world.bounds
    // (which extends further down on purpose so this is reachable at all —
    // see Game.js create()).
    if (this.scene.levelData && this.y > this.scene.levelData.heightPx + 40) {
      this.scene.events.emit('player-fell');
    }
  }
}
